import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  Dispatch,
  SetStateAction,
} from "react";
import { FontIcon } from "react-md";
import { Button } from "@material-ui/core";
import { TextInput, Modal } from "@mantine/core";
import GoogleMapReact from "google-map-react";
import NavigoDashboardUtils from "../../services/NavigoDashboardUtils";
import pinIconRed from "../../assets/map-pin-red.png";
import pinIconBlack from "../../assets/map-pin-black.png";

const navigoDashboardUtils = new NavigoDashboardUtils();

/* =========================
   Types
========================= */

export interface LatLng {
  lat: number;
  lng: number;
}

export interface PlaceSuggestion {
  placeId: string;
  label: string;
}

export interface LocationDialogResult {
  confirmed: boolean;
  latLng?: LatLng | null;
  address?: any;
  selectedLabel?: string;
  query?: string;
}

export interface LocationDialogState {
  visible: boolean;
  resolve?: (value: LocationDialogResult) => void;

  query?: string;
  selectedLabel?: string;
  latLng?: LatLng | null;
  address?: any;

  suggestions?: PlaceSuggestion[];
  loading?: boolean;
  sessionToken?: string;
}

/* =========================
   Helpers
========================= */

function generateSessionToken(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/* =========================
   API HELPERS
========================= */

/**
 * Autocomplete places
 */
function fetchPlaces(
  query: string,
  sessionToken: string,
): Promise<PlaceSuggestion[]> {
  if (!query || query.length < 3) return Promise.resolve([]);

  return new Promise((resolve, reject) => {
    navigoDashboardUtils.getPlacesAutocomplete(
      {
        input: query,
        includedRegionCodes: ["in"],
        languageCode: "en",
        sessionToken,
      },
      (res: any) => {
        const list: PlaceSuggestion[] = (res?.suggestions || []).map(
          (item: any) => {
            const p = item.placePrediction;
            const main = p?.structuredFormat?.mainText?.text || "";
            const secondary = p?.structuredFormat?.secondaryText?.text || "";

            return {
              placeId: p.placeId,
              label: secondary ? `${main}, ${secondary}` : main,
            };
          },
        );

        resolve(list);
      },
      (err: any) => reject(err),
    );
  });
}

/**
 * placeId → coordinates
 */
function getLatLngByPlaceId(
  placeId: string,
  sessionToken: string,
): Promise<LatLng> {
  if (!placeId) return Promise.reject("placeId required");

  return new Promise((resolve, reject) => {
    navigoDashboardUtils.getCoordinatesByPlaceId(
      { placeId, sessionToken },
      (res: any) => {
        if (res?.latitude == null || res?.longitude == null) {
          reject("Invalid coordinates response");
          return;
        }

        resolve({
          lat: res.latitude,
          lng: res.longitude,
        });
      },
      (err: any) => reject(err),
    );
  });
}

/**
 * coordinates → address
 */
function getAddressByLatLng({ lat, lng }: LatLng): Promise<any> {
  if (lat == null || lng == null) {
    return Promise.reject("lat/lng required");
  }

  return new Promise((resolve, reject) => {
    navigoDashboardUtils.getAddressByCoordinates(
      { latitude: lat, longitude: lng },
      (res: any) => resolve(res),
      (err: any) => reject(err),
    );
  });
}

/* =========================
   Hook
========================= */

export function useLocationDialog() {
  const [dialogState, setDialogState] = useState<LocationDialogState | null>(
    null,
  );

  const openLocationDialog = useCallback(
    (initialData: Partial<LocationDialogState | null> = {}) => {
      const safeData = initialData || {};

      return new Promise<LocationDialogResult>((resolve) => {
        setDialogState({
          visible: true,
          resolve,

          query: safeData.query || "",
          selectedLabel: safeData.selectedLabel || "",
          latLng: safeData.latLng || null,
          address: safeData.address || null,

          suggestions: [],
          loading: false,
          sessionToken: generateSessionToken(),
        });
      });
    },
    [],
  );

  const closeDialog = useCallback(
    (result: LocationDialogResult) => {
      if (dialogState?.resolve) {
        dialogState.resolve(result);
      }
      setDialogState(null);
    },
    [dialogState],
  );

  return {
    dialogState,
    setDialogState,
    openLocationDialog,
    closeDialog,
  };
}

/* =========================
   Map Pin
========================= */

interface PinProps {
  lat: number;
  lng: number;
}

const Pin: React.FC<PinProps> = () => (
  <img
    src={pinIconRed}
    alt="Map Pin"
    style={{
      position: "absolute",
      width: 36,
      height: 36,
      transform: "translate(-50%, -100%)",
      pointerEvents: "none",
    }}
  />
);

/* =========================
   Dialog Component
========================= */

interface LocationSearchDialogProps {
  dialogState: LocationDialogState | null;
  setDialogState: Dispatch<SetStateAction<LocationDialogState | null>>;
  closeDialog: (result: LocationDialogResult) => void;
}

export const LocationSearchDialog: React.FC<LocationSearchDialogProps> = ({
  dialogState,
  setDialogState,
  closeDialog,
}) => {
  const visible = dialogState?.visible || false;
  const query = dialogState?.query || "";
  const selectedLabel = dialogState?.selectedLabel || "";
  const suggestions = dialogState?.suggestions || [];
  const latLng = dialogState?.latLng || null;
  const address = dialogState?.address || null;
  const loading = dialogState?.loading || false;
  const sessionToken = dialogState?.sessionToken || "";

  const textFieldRef = useRef<any>(null);

  /* =========================
     Auto-focus TextField
  ========================== */
  useEffect(() => {
    if (visible && textFieldRef.current) {
      setTimeout(() => {
        textFieldRef.current.focus();
      }, 100);
    }
  }, [visible]);

  /* =========================
   Fetch address for pre-seeded latLng (initialData)
========================= */
  useEffect(() => {
    if (!visible || !latLng || address) return;

    (async () => {
      setDialogState((prev) => (prev ? { ...prev, loading: true } : prev));

      try {
        const addr = await getAddressByLatLng(latLng);

        setDialogState((prev) =>
          prev
            ? {
                ...prev,
                address: addr,
                loading: false,
              }
            : prev,
        );
      } catch (e) {
        console.error(e);

        setDialogState((prev) => (prev ? { ...prev, loading: false } : prev));
      }
    })();
  }, [visible]);

  /* =========================
     Autocomplete Effect
  ========================== */
  useEffect(() => {
    if (query === "") {
      setDialogState((prev) => (prev ? { ...prev, suggestions: [] } : prev));
      return;
    }

    if (!visible || query.length < 3) return;

    const timer = setTimeout(async () => {
      try {
        const list = await fetchPlaces(query, sessionToken);
        setDialogState((prev) =>
          prev ? { ...prev, suggestions: list } : prev,
        );
      } catch (e) {
        console.error(e);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, visible, setDialogState]);

  /* =========================
     Select Place
  ========================== */
  const handleSelectPlace = async (item: PlaceSuggestion) => {
    setDialogState((prev) =>
      prev
        ? {
            ...prev,
            loading: true,
            suggestions: [],
            selectedLabel: item.label,
          }
        : prev,
    );

    try {
      const coords = await getLatLngByPlaceId(item.placeId, sessionToken);
      const addr = await getAddressByLatLng(coords);

      setDialogState((prev) =>
        prev
          ? {
              ...prev,
              latLng: coords,
              address: addr,
              loading: false,
              sessionToken: generateSessionToken(),
            }
          : prev,
      );
    } catch (e) {
      console.error(e);
      setDialogState((prev) => (prev ? { ...prev, loading: false } : prev));
    }
  };

  if (!visible) return null;

  return (
    <Modal
      id="location-search-dialog"
      size={"70%"}
      centered
      style={{ position: "fixed" }}
      opened
      withCloseButton={false}
      // onClose={() => closeDialog({ confirmed: false })}
      onClose={() => {}}
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <h3 style={{ margin: 0 }}>Search location</h3>
        <div>
          {!latLng || loading ? null : (
            <Button
              style={{
                width: "100px",
                height: "40px",

                color: "#ffffff",
                backgroundColor: "#ef7c01",
              }}
              disabled={!latLng || loading}
              disableRipple
              disableFocusRipple
              onClick={() =>
                closeDialog({
                  confirmed: true,
                  latLng,
                  address,
                  selectedLabel,
                  query,
                })
              }
            >
              Confirm
            </Button>
          )}

          <Button
            onClick={() => closeDialog({ confirmed: false })}
            style={{
              width: "100px",
              height: "40px",
              marginLeft: 10,
              color: "#ffffff",
              backgroundColor: "#c2c2c2",
            }}
            disableRipple
            disableFocusRipple
          >
            Cancel
          </Button>
        </div>
      </div>

      {/* Map + Search */}
      <div style={{ position: "relative", marginTop: 10, height: 350 }}>
        {/* Search Box */}
        <div
          style={{
            position: "absolute",
            zIndex: 2,
            width: "calc(100% - 20px)",
            maxWidth: 520,
            left: 10,
            top: 10,
            background: "#fff",
            borderRadius: "8px",
            padding: "12px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div style={{ position: "relative" }}>
            <TextInput
              ref={textFieldRef}
              placeholder="Search location"
              value={selectedLabel || query}
              onChange={(e) =>
                setDialogState((prev) =>
                  prev
                    ? {
                        ...prev,
                        query: e.currentTarget.value,
                        selectedLabel: "",
                      }
                    : prev,
                )
              }
              styles={{ input: { paddingRight: 36 } }}
            />

            {(query || selectedLabel) && (
              <button
                type="button"
                onClick={() =>
                  setDialogState((prev) =>
                    prev
                      ? {
                          ...prev,
                          query: "",
                          selectedLabel: "",
                          suggestions: [],
                        }
                      : prev,
                  )
                }
                title="Clear search"
                style={{
                  position: "absolute",
                  right: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  fontSize: 18,
                  cursor: "pointer",
                  color: "#999",
                  lineHeight: 1,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#333")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "#999")}
              >
                ✕
              </button>
            )}
          </div>

          {suggestions.length > 0 && (
            <div
              style={{
                border: "1px solid #ccc",
                maxHeight: 200,
                overflowY: "auto",
                marginTop: 8,
                borderRadius: 4,
              }}
            >
              {suggestions.map((item) => (
                <div
                  key={item.placeId}
                  style={{
                    padding: 8,
                    cursor: "pointer",
                    borderBottom: "1px solid #eee",
                  }}
                  onClick={() => handleSelectPlace(item)}
                >
                  <img
                    src={pinIconBlack}
                    alt="pin"
                    style={{ width: 18, marginRight: 6 }}
                  />
                  {item.label}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Map */}
        <GoogleMapReact
          bootstrapURLKeys={{ key: "AIzaSyDvCpz50pYwkg63zlD6iCodT75AGpqIhxc" }}
          center={latLng || { lat: 28.6139, lng: 77.209 }}
          zoom={14}
          onClick={async (event: { lat: number; lng: number }) => {
            if (event.lat == null || event.lng == null) return;

            const coords = { lat: event.lat, lng: event.lng };

            setDialogState((prev) =>
              prev
                ? {
                    ...prev,
                    latLng: coords,
                    loading: true,
                    sessionToken: generateSessionToken(),
                  }
                : prev,
            );

            try {
              const addr = await getAddressByLatLng(coords);
              setDialogState((prev) =>
                prev ? { ...prev, address: addr, loading: false } : prev,
              );
            } catch (e) {
              console.error(e);
              setDialogState((prev) =>
                prev ? { ...prev, loading: false } : prev,
              );
            }
          }}
        >
          {latLng && <Pin lat={latLng.lat} lng={latLng.lng} />}
        </GoogleMapReact>
      </div>

      {/* Info */}
      {address && (
        <div style={{ marginTop: 12 }}>
          <strong>Address:</strong>
          <div>{address?.housing || "No location selected"}</div>
        </div>
      )}

      {latLng && (
        <div style={{ marginTop: 12 }}>
          <strong>Location selected:</strong>
          <div>{"Latitude: " + (latLng?.lat || "Not selected")}</div>
          <div>{"Longitude: " + (latLng?.lng || "Not selected")}</div>
        </div>
      )}
    </Modal>
  );
};
